import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/contexts/CartContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import CartDrawer from "@/components/cart/CartDrawer";
import ScrollToTop from "@/components/ScrollToTop";
import { PageLoadingFallback } from "@/components/LoadingFallback";
import Index from "./pages/Index";
import { landingPages } from "@/data/landingPages";



// Lazy-loaded routes — keeps initial bundle small and speeds up first paint.
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProgramsPage = lazy(() => import("./pages/ProgramsPage"));
const AfterSchoolPage = lazy(() => import("./pages/AfterSchoolPage"));
const HolidayCampsPage = lazy(() => import("./pages/HolidayCampsPage"));
const ClubsPage = lazy(() => import("./pages/ClubsPage"));
const EarlyCarePage = lazy(() => import("./pages/EarlyCarePage"));
const SummerCampsPage = lazy(() => import("./pages/SummerCampsPage"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Policies = lazy(() => import("./pages/Policies"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const InstructorDashboard = lazy(() => import("./pages/InstructorDashboard"));
const StaffDashboard = lazy(() => import("./pages/StaffDashboard"));
const Checkout = lazy(() => import("./pages/Checkout"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const UpdatePhone = lazy(() => import("./pages/UpdatePhone"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const PhoenixSummerCampGuide = lazy(() => import("./pages/PhoenixSummerCampGuide"));
const PhoenixAfterSchoolGuide = lazy(() => import("./pages/PhoenixAfterSchoolGuide"));



const existingPaths = new Set([
  "", "auth", "reset-password", "accept-invite", "unsubscribe", "update-phone", "dashboard",
  "programs", "after-school", "holiday-camps", "early-care", "clubs",
  "summer-camps", "contact", "about", "policies", "admin", "instructor",
  "staff", "checkout",
]);
const seoLandingRoutes = landingPages.filter((p) => !existingPaths.has(p.slug));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 minute — avoids needless refetches across components
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner duration={3000} />
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <CartProvider>
              <CartDrawer />
              <Suspense fallback={<PageLoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/update-phone" element={<UpdatePhone />} />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/programs" element={<ProgramsPage />} />
                  <Route path="/after-school" element={<AfterSchoolPage />} />
                  <Route path="/holiday-camps" element={<HolidayCampsPage />} />
                  <Route path="/early-care" element={<EarlyCarePage />} />
                  <Route path="/clubs" element={<ClubsPage />} />
                  <Route path="/summer-camps" element={<SummerCampsPage />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/policies" element={<Policies />} />
                  <Route path="/admin" element={
                    <ProtectedRoute requiredRoles={['admin', 'staff', 'instructor']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/instructor" element={
                    <ProtectedRoute requiredRoles={['admin', 'instructor']}>
                      <InstructorDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/staff" element={
                    <ProtectedRoute requiredRoles={['admin', 'staff']}>
                      <StaffDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  } />
                  <Route path="/phoenix-summer-camp-guide" element={<PhoenixSummerCampGuide />} />
                  <Route path="/phoenix-after-school-guide" element={<PhoenixAfterSchoolGuide />} />
                  {seoLandingRoutes.map((p) => (
                    <Route key={p.slug} path={`/${p.slug}`} element={<LandingPage />} />
                  ))}
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />

                </Routes>
              </Suspense>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
